import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Power, Loader2, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Supplier {
  id: string;
  name: string;
  website: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const truncate = (s: string, n: number) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

export default function PlatformSuppliers() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const adminId = session?.user?.id ?? null;

  const suppliersQuery = useQuery({
    queryKey: ["platform-suppliers"],
    queryFn: async (): Promise<Supplier[]> => {
      const { data, error } = await supabase
        .from("platform_suppliers")
        .select("*")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Supplier[];
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel("admin-platform-suppliers")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "platform_suppliers" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["platform-suppliers"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const suppliers = suppliersQuery.data ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<{ name?: string; website?: string }>({});

  const [deactivateTarget, setDeactivateTarget] = useState<Supplier | null>(null);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setWebsite("");
    setIsActive(true);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (s: Supplier) => {
    setEditing(s);
    setName(s.name);
    setWebsite(s.website ?? "");
    setIsActive(s.is_active);
    setErrors({});
    setDialogOpen(true);
  };

  const writeAudit = async (
    action:
      | "platform_supplier_created"
      | "platform_supplier_updated"
      | "platform_supplier_deactivated"
      | "platform_supplier_activated",
    entityId: string,
    details: Record<string, unknown>
  ) => {
    if (!adminId) return;
    await supabase.from("admin_audit_logs").insert({
      admin_id: adminId,
      action,
      entity_type: "platform_suppliers",
      entity_id: entityId,
      details: details as never,
    });
  };

  const validate = (): boolean => {
    const errs: { name?: string; website?: string } = {};
    if (!name.trim()) errs.name = "Name is required";
    if (name.trim().length > 100) errs.name = "Max 100 characters";
    const w = website.trim();
    if (w && !/^https?:\/\//i.test(w)) {
      errs.website = "Must start with http:// or https://";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (editing) {
        const { error } = await supabase
          .from("platform_suppliers")
          .update({
            name: name.trim(),
            website: website.trim() || null,
            is_active: isActive,
            updated_by: adminId,
          })
          .eq("id", editing.id)
          .select();
        if (error) throw error;
        await writeAudit("platform_supplier_updated", editing.id, {
          name: name.trim(),
          website: website.trim() || null,
          is_active: isActive,
        });
        toast.success("Supplier updated");
      } else {
        const { data, error } = await supabase
          .from("platform_suppliers")
          .insert({
            name: name.trim(),
            website: website.trim() || null,
            is_active: true,
            created_by: adminId,
            updated_by: adminId,
          })
          .select()
          .single();
        if (error) throw error;
        const newSupplier = data as Supplier;
        await writeAudit("platform_supplier_created", newSupplier.id, {
          name: name.trim(),
          website: website.trim() || null,
        });
        toast.success("Supplier created");
      }
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["platform-suppliers"] });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e?.code === "23505") {
        toast.error("A supplier with this name already exists.");
      } else {
        toast.error(e?.message || "Failed to save");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (s: Supplier) => {
    try {
      const { error } = await supabase
        .from("platform_suppliers")
        .update({ is_active: true, updated_by: adminId })
        .eq("id", s.id)
        .select();
      if (error) throw error;
      await writeAudit("platform_supplier_activated", s.id, { name: s.name });
      toast.success("Supplier activated");
      queryClient.invalidateQueries({ queryKey: ["platform-suppliers"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to activate");
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    const s = deactivateTarget;
    try {
      const { error } = await supabase
        .from("platform_suppliers")
        .update({ is_active: false, updated_by: adminId })
        .eq("id", s.id)
        .select();
      if (error) throw error;
      await writeAudit("platform_supplier_deactivated", s.id, { name: s.name });
      toast.success("Supplier deactivated");
      setDeactivateTarget(null);
      queryClient.invalidateQueries({ queryKey: ["platform-suppliers"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to deactivate");
    }
  };

  return (
    <>
      <div className="flex items-start justify-between mb-4 gap-4">
        <p className="text-slate-400 text-sm">
          Manage platform-wide suppliers used as price sources for system line items.
        </p>
        <Button
          onClick={openCreate}
          className="bg-amber-500 hover:bg-amber-600 text-slate-950 shrink-0"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Supplier
        </Button>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-md">
        {suppliersQuery.isLoading ? (
          <div className="p-8 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : suppliers.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            No suppliers yet. Add your first supplier.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                  Name
                </th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                  Website
                </th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                  Status
                </th>
                <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-400 font-medium w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr
                  key={s.id}
                  className="bg-slate-900 hover:bg-slate-800/50 border-b border-slate-800/60"
                >
                  <td className="px-4 py-2 text-white font-medium">{s.name}</td>
                  <td className="px-4 py-2">
                    {s.website ? (
                      <a
                        href={s.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300"
                      >
                        {truncate(s.website, 40)}
                        <ExternalLink className="h-3 w-3 inline" />
                      </a>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {s.is_active ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/50 text-slate-400 border border-slate-600">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
                        onClick={() => openEdit(s)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={
                          s.is_active
                            ? "h-7 w-7 text-amber-400 hover:text-amber-300 hover:bg-slate-800"
                            : "h-7 w-7 text-green-400 hover:text-green-300 hover:bg-slate-800"
                        }
                        onClick={() =>
                          s.is_active ? setDeactivateTarget(s) : handleActivate(s)
                        }
                      >
                        <Power className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? "Edit Supplier" : "Add Supplier"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Platform supplier used as price source for system line items.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Name</Label>
              <Input
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
              {errors.name && <p className="text-xs text-red-400">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300">Website</Label>
              <Input
                value={website}
                placeholder="https://www.supplier.co.za"
                onChange={(e) => setWebsite(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
              {errors.website && (
                <p className="text-xs text-red-400">{errors.website}</p>
              )}
            </div>

            {editing && (
              <div className="flex items-center justify-between rounded-md border border-slate-700 bg-slate-800 px-3 py-2">
                <div>
                  <Label className="text-slate-300">Active</Label>
                  <p className="text-xs text-slate-400">
                    Inactive suppliers are hidden from price source selection.
                  </p>
                </div>
                <Switch checked={isActive} onCheckedChange={setIsActive} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deactivateTarget !== null}
        onOpenChange={(o) => !o && setDeactivateTarget(null)}
      >
        <AlertDialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Deactivate this supplier?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {deactivateTarget?.name} will be hidden from price source selection.
              You can reactivate it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
